/* ===========================================================
   JSON-LD (schema.org) por página — renderizado no CORPO, igual ao
   FAQPage da landing (index.tsx). Fica no DOM, então o prerender o
   serializa. A home já injeta Organization+WebSite+SoftwareApplication
   no index.html; aqui adicionamos o schema específico de cada página
   (DefinedTerm, Article, WebApplication, BreadcrumbList).
   =========================================================== */
import { SITE, urlCanonica } from "./site";

/** Tag <script type="application/ld+json"> com o objeto serializado. */
export function JsonLd({ data }: { data: unknown }) {
  return (
    <script
      type="application/ld+json"
      // mesma técnica do FAQ_JSONLD da landing — string estática, sem input do usuário
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

const ORG_REF = { "@id": `${SITE.base}/#organization` } as const;

/** BreadcrumbList a partir de [{ nome, path }] na ordem raiz→folha. */
export function breadcrumbLd(itens: ReadonlyArray<{ nome: string; path: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: itens.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.nome,
      item: urlCanonica(it.path),
    })),
  };
}

/** DefinedTerm (verbete de glossário) ancorado ao DefinedTermSet do site. */
export function definedTermLd(args: { termo: string; definicao: string; path: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name: args.termo,
    description: args.definicao,
    url: urlCanonica(args.path),
    inDefinedTermSet: {
      "@type": "DefinedTermSet",
      name: "Glossário de lucro e tráfego pago no e-commerce",
      url: urlCanonica("/blog"),
    },
    publisher: ORG_REF,
  };
}

/** Article (post do blog) com datas e autoria da organização. */
export function articleLd(args: {
  titulo: string;
  descricao: string;
  path: string;
  publicadoEm: string;
  atualizadoEm: string;
}) {
  const url = urlCanonica(args.path);
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: args.titulo,
    description: args.descricao,
    inLanguage: "pt-BR",
    datePublished: args.publicadoEm,
    dateModified: args.atualizadoEm,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    author: { "@type": "Organization", name: SITE.nome, url: SITE.base + "/" },
    publisher: ORG_REF,
  };
}

/** WebApplication (calculadora pública gratuita). */
export function webAppLd(args: { nome: string; descricao: string; path: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: args.nome,
    description: args.descricao,
    url: urlCanonica(args.path),
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web",
    inLanguage: "pt-BR",
    isAccessibleForFree: true,
    offers: { "@type": "Offer", price: "0", priceCurrency: "BRL" },
    publisher: ORG_REF,
  };
}
